// ============================================================================
// PECA REDLINE VIEW
// ARQUIVO: src/features/files/PecaRedlineView.jsx
// RESPONSABILIDADE: Fase C4 do PRE_VOO_LIVRO_PROCESSUAL_GERADO_20260901.md —
//   compara as 2 últimas revisões do .md da "Peça em Si" (histórico nativo
//   do Drive, via getPecaMarkdownRedline) e mostra o que mudou: inserção
//   com fundo azul, remoção riscada vermelha, resto em preto.
//
// Limitação conhecida, aceita de propósito (confirmada com o titular
// 02/09/2026): texto simples, SEM formatação rica (negrito/título) junto
// com a marcação de cor — diff palavra-por-palavra de markdown formatado
// ao mesmo tempo é bem mais complexo; entrega funcionando simples primeiro.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { diffWordsWithSpace } from "diff";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { functions } from "../../shared/api/firebase/config";
import { markdownComponents, sanitizeSchema, handleCopyConteudoCaderno } from "./CadernoMarkdownViewer";

function extractDriveFileId(webViewLink) {
  const m = String(webViewLink || "").match(/\/file\/d\/([^/]+)/);
  return m ? m[1] : "";
}

// Fase C4-bis (02/09/2026, pedido do titular ao ver o redline pela 1ª vez):
// em vez de <pre> com texto cru, envolve cada trecho inserido/removido em
// <ins>/<del> (tags HTML padrão, já liberadas no schema default do
// rehype-sanitize — não precisou estender nada) e manda o markdown INTEIRO
// pro MESMO pipeline de renderização da aba Normal (CadernoMarkdownViewer).
// CommonMark trata <ins>/<del> como HTML inline "transparente" — o que
// estiver dentro continua sendo parseado como markdown normal (negrito,
// itálico, link), então "<ins>**mudou**</ins>" sai em negrito E sublinhado.
//
// Limite conhecido, não resolvido (documentar, não esconder): só funciona
// bem pra edição PALAVRA A PALAVRA dentro de um parágrafo já existente —
// que é o caso comum ao revisar uma minuta. Se um trecho inserido for uma
// LINHA DE BLOCO nova inteira (ex.: um "## Título Novo" que não existia
// antes), o marcador de bloco (#, >, -) fica preso depois da tag <ins> no
// meio da linha, então deixa de ser reconhecido como heading/blockquote/
// lista pelo parser — aparece como texto cru nesse caso específico. Corrigir
// isso de verdade exigiria diff estrutural em cima da árvore do markdown
// (AST), não diff de string — escopo bem maior, avaliar depois se fizer
// falta na prática.
function anotarDiffComoMarkdown(partes) {
  const wrap = (texto, tag) => {
    if (!texto) return "";
    // nunca deixa a tag atravessar quebra de parágrafo (linha em branco) —
    // isso preserva a fronteira de bloco do markdown intacta.
    return texto
      .split(/(\n{2,})/)
      .map((pedaco) => {
        if (!pedaco) return "";
        if (/^\n{2,}$/.test(pedaco)) return pedaco;
        return `<${tag}>${pedaco}</${tag}>`;
      })
      .join("");
  };
  return (partes || [])
    .map((parte) => {
      if (parte.added) return wrap(parte.value, "ins");
      if (parte.removed) return wrap(parte.value, "del");
      return parte.value;
    })
    .join("");
}

const redlineComponents = {
  ...markdownComponents,
  ins: (props) => (
    <ins
      className="bg-blue-100 text-blue-900 no-underline rounded-sm px-0.5"
      {...props}
    />
  ),
  del: (props) => (
    <del
      className="bg-red-100 text-red-700 rounded-sm px-0.5"
      {...props}
    />
  ),
};

// `onOpenNovaJanela` (opcional, 20/09/2026 — pedido do titular): o modo Normal
// (CadernoMarkdownViewer) sempre teve "Nova Janela" + "Abrir no Drive" no topo;
// o Redline só tinha "Atualizar". Agora os três ficam na mesma linha, na mesma
// ordem nos dois modos (simetria — o titular é autista): Nova Janela · Abrir
// no Drive · Atualizar.
export function PecaRedlineView({ driveWebViewLink, onOpenNovaJanela }) {
  const [partes, setPartes] = useState(null);
  const [semComparacao, setSemComparacao] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  // Botão "Atualizar" (02/09/2026, achado ao vivo pelo titular): revisão
  // nova no Drive pode levar alguns segundos pra aparecer em
  // revisions.list (consistência eventual da API) — sem isso, só dava pra
  // forçar nova busca saindo da aba e voltando. refreshTick incrementa a
  // dependência do effect sem precisar de nenhum outro estado.
  const [refreshTick, setRefreshTick] = useState(0);

  // Achado ao vivo (16/09/2026, titular): clicar "Atualizar" trocava o
  // painel inteiro pelo estado de loading ("Comparando versões...", bem
  // mais curto que o texto real), o que zera o scroll do container — e ao
  // voltar o conteúdo completo, o scroll não era restaurado, jogando o
  // titular de volta pro topo da peça. contentRef aponta pro painel
  // rolável; scrollPosRef guarda a posição só no clique manual de
  // "Atualizar" (nunca no carregamento inicial nem na troca de peça, que
  // devem mesmo começar do topo).
  const contentRef = useRef(null);
  const scrollPosRef = useRef(null);

  const handleAtualizar = () => {
    if (contentRef.current) {
      scrollPosRef.current = contentRef.current.scrollTop;
    }
    setRefreshTick((n) => n + 1);
  };

  useEffect(() => {
    if (!loading && scrollPosRef.current !== null && contentRef.current) {
      contentRef.current.scrollTop = scrollPosRef.current;
      scrollPosRef.current = null;
    }
  }, [loading]);

  useEffect(() => {
    const fileId = extractDriveFileId(driveWebViewLink);
    if (!fileId) {
      setErro("Peça ainda não foi criada para este documento.");
      return;
    }
    let cancelado = false;
    setLoading(true);
    setErro("");
    setPartes(null);
    setSemComparacao(false);
    (async () => {
      try {
        const fn = httpsCallable(functions, "getPecaMarkdownRedline");
        const payload = await fn({ driveFileId: fileId });
        const { atual, anterior } = payload?.data || {};
        if (cancelado) return;
        if (typeof anterior !== "string") {
          setSemComparacao(true);
          return;
        }
        setPartes(diffWordsWithSpace(anterior, atual || ""));
      } catch (e) {
        console.error("[PecaRedlineView]", e);
        if (!cancelado) setErro(String(e?.message || e));
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [driveWebViewLink, refreshTick]);

  const markdownAnotado = useMemo(() => anotarDiffComoMarkdown(partes), [partes]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-end gap-3 mb-2 shrink-0">
        {onOpenNovaJanela ? (
          <button
            type="button"
            onClick={onOpenNovaJanela}
            className="text-[10px] font-bold text-gray-400 hover:text-gray-600 uppercase tracking-wide whitespace-nowrap"
            title="Abrir esta tela numa janela separada"
          >
            <i className="fas fa-up-right-from-square mr-1"></i> Nova Janela
          </button>
        ) : null}
        {driveWebViewLink ? (
          <a
            href={driveWebViewLink}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] font-bold text-[#C5A059] hover:text-[#a8844a] uppercase tracking-wide whitespace-nowrap"
            title="Abrir no Drive (editar/baixar/comentar)"
          >
            <i className="fab fa-google-drive mr-1"></i> Abrir no Drive ↗
          </a>
        ) : null}
        <button
          type="button"
          onClick={handleAtualizar}
          disabled={loading}
          className="text-[10px] font-bold text-[#C5A059] hover:text-[#a8844a] uppercase tracking-wide whitespace-nowrap disabled:opacity-40"
          title="Buscar de novo as 2 últimas revisões no Drive"
        >
          <i className={`fas fa-rotate mr-1 ${loading ? "fa-spin" : ""}`}></i> Atualizar
        </button>
      </div>
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto custom-scrollbar border border-gray-200 rounded-lg px-6 py-4 bg-white"
        onCopy={handleCopyConteudoCaderno}
      >
        {loading ? (
          <div className="text-center text-xs text-gray-400 py-16">
            <i className="fas fa-spinner fa-spin mr-1.5"></i> Comparando versões...
          </div>
        ) : erro ? (
          <div className="text-center text-xs text-red-600 py-16">
            <i className="fas fa-triangle-exclamation mr-1.5"></i> {erro}
          </div>
        ) : semComparacao ? (
          <div className="text-center text-xs text-gray-400 py-16">
            Esta peça ainda só tem uma versão salva — nada pra comparar ainda.
            <br />
            O redline aparece a partir da 2ª vez que o texto for alterado.
          </div>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
            components={redlineComponents}
          >
            {markdownAnotado}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
}
